import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';

import type { AgentChatStreamEvent, AgentCitation } from '../api/services/agent';
import type {
  RetrievalContextSource,
  RetrievalExecutionPhase,
  RetrievalGraphEdge,
  RetrievalGraphNode,
  RetrievalPlan,
  RetrievalPlanStep,
  RetrievalStreamStatus,
  RetrievalThinkingLog,
  RetrievalTimelineEntry,
  RetrievalVerification,
} from '../types/retrieval';

const MAX_TIMELINE_ENTRIES = 300;

export type RetrievalPlanCommandType = 'execute' | 'pause' | 'resume' | 'stop' | 'save_plan';

export interface RetrievalPlanCommand {
  id: string;
  type: RetrievalPlanCommandType;
  payload?: Record<string, unknown>;
}

interface RetrievalState {
  sessionId: string | null;
  streamStatus: RetrievalStreamStatus;
  activeQuery: string | null;
  lastError: string | null;
  plan: RetrievalPlan | null;
  planOverlayVisible: boolean;
  isPlanEditing: boolean;
  executionPhase: RetrievalExecutionPhase;
  pendingPlanCommand: RetrievalPlanCommand | null;
  graphNodes: RetrievalGraphNode[];
  graphEdges: RetrievalGraphEdge[];
  selectedNodeId: string | null;
  focusedCitationRef: string | null;
  contextSources: Record<string, RetrievalContextSource>;
  timeline: RetrievalTimelineEntry[];
  streamingText: string;
  generatedText: string;
  citations: AgentCitation[];
  gaps: string[];
  verification: RetrievalVerification | null;
  verificationResults: RetrievalVerification[];
  thinkingLogs: RetrievalThinkingLog[];
  confidenceScore: number | null;
  updatedAt: string | null;
}

interface RetrievalStateContextValue {
  state: RetrievalState;
  reset: (opts?: { keepSession?: boolean }) => void;
  setSessionId: (sessionId: string | null) => void;
  startQuery: (query: string) => void;
  setPlan: (plan: RetrievalPlan | null, opts?: { openOverlay?: boolean }) => void;
  ingestEvent: (event: AgentChatStreamEvent) => void;
  selectNode: (nodeId: string | null) => void;
  focusCitationRef: (citationRef: string | null) => void;
  setPlanOverlayVisible: (visible: boolean) => void;
  setPlanEditing: (editing: boolean) => void;
  setExecutionPhase: (phase: RetrievalExecutionPhase) => void;
  requestPlanCommand: (type: RetrievalPlanCommandType, payload?: Record<string, unknown>) => string;
  acknowledgePlanCommand: (commandId: string) => void;
  reorderPlanSteps: (fromIndex: number, toIndex: number) => void;
  removePlanStep: (stepId: string) => void;
  addPlanStep: (step: RetrievalPlanStep, atIndex?: number) => void;
  updatePlanGoal: (goal: string) => void;
}

type RetrievalAction =
  | { type: 'reset'; keepSession: boolean }
  | { type: 'set_session_id'; sessionId: string | null }
  | { type: 'start_query'; query: string }
  | { type: 'set_plan'; plan: RetrievalPlan | null; openOverlay: boolean }
  | { type: 'ingest_event'; event: AgentChatStreamEvent }
  | { type: 'select_node'; nodeId: string | null }
  | { type: 'focus_citation'; citationRef: string | null }
  | { type: 'set_plan_overlay_visible'; visible: boolean }
  | { type: 'set_plan_editing'; editing: boolean }
  | { type: 'set_execution_phase'; phase: RetrievalExecutionPhase }
  | { type: 'request_plan_command'; command: RetrievalPlanCommand }
  | { type: 'acknowledge_plan_command'; commandId: string }
  | { type: 'reorder_plan_steps'; fromIndex: number; toIndex: number }
  | { type: 'remove_plan_step'; stepId: string }
  | { type: 'add_plan_step'; step: RetrievalPlanStep; atIndex?: number }
  | { type: 'update_plan_goal'; goal: string };

const RetrievalStateContext = createContext<RetrievalStateContextValue | null>(null);

const buildDefaultPlan = (query: string): RetrievalPlan => {
  const now = new Date().toISOString();
  return {
    goal: query,
    requiresApproval: true,
    updatedAt: now,
    steps: [
      {
        id: 'analyze-query',
        type: 'analyze_query',
        description: 'Analyze query intent and retrieval scope',
        status: 'pending',
      },
      {
        id: 'search-graph',
        type: 'search_graph',
        description: 'Traverse graph neighborhoods for relevant entities',
        status: 'pending',
      },
      {
        id: 'search-retrieval',
        type: 'retrieve',
        description: 'Run hybrid retrieval over vector and graph context',
        status: 'pending',
      },
      {
        id: 'generate-answer',
        type: 'generate',
        description: 'Generate grounded answer with citations',
        status: 'pending',
      },
      {
        id: 'verify-answer',
        type: 'verify',
        description: 'Verify grounding and confidence',
        status: 'pending',
      },
    ],
  };
};

const normalizePlanStepStatus = (status: unknown): RetrievalPlanStep['status'] => {
  const normalized = normalizeString(status)?.toLowerCase();
  switch (normalized) {
    case 'in_progress':
      return 'running';
    case 'succeeded':
      return 'completed';
    case 'pending':
    case 'running':
    case 'completed':
    case 'failed':
    case 'blocked':
    case 'skipped':
      return normalized;
    default:
      return 'pending';
  }
};

const initialRetrievalState: RetrievalState = {
  sessionId: null,
  streamStatus: 'idle',
  activeQuery: null,
  lastError: null,
  plan: null,
  planOverlayVisible: false,
  isPlanEditing: false,
  executionPhase: 'idle',
  pendingPlanCommand: null,
  graphNodes: [],
  graphEdges: [],
  selectedNodeId: null,
  focusedCitationRef: null,
  contextSources: {},
  timeline: [],
  streamingText: '',
  generatedText: '',
  citations: [],
  gaps: [],
  verification: null,
  verificationResults: [],
  thinkingLogs: [],
  confidenceScore: null,
  updatedAt: null,
};

const normalizeAttributes = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
};

const normalizeString = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
};

const toRetrievalPlan = (value: unknown): RetrievalPlan | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const raw = value as Record<string, unknown>;
  const goal = normalizeString(raw.goal);
  const rawSteps = Array.isArray(raw.steps) ? raw.steps : [];
  const steps: RetrievalPlanStep[] = rawSteps
    .filter((step): step is Record<string, unknown> => !!step && typeof step === 'object')
    .map((step, index) => {
      const id = normalizeString(step.id) || `step-${index + 1}`;
      const description = normalizeString(step.description) || 'Plan step';
      const type = normalizeString(step.type) || 'tool_invocation';
      return {
        id,
        type,
        description,
        status: normalizePlanStepStatus(step.status),
        resultSummary:
          normalizeString(
            (step.metadata as Record<string, unknown> | undefined)?.result_summary ??
              (step.result as Record<string, unknown> | undefined)?.summary,
          ) ?? null,
        metadata: normalizeAttributes(step.metadata),
      };
    });

  if (!goal) {
    return null;
  }
  const approvalRaw = raw.approval as Record<string, unknown> | undefined;
  const requiresApproval = Boolean(approvalRaw?.required ?? false);
  return {
    id: normalizeString(raw.id) || undefined,
    goal,
    requiresApproval,
    steps,
    updatedAt: new Date().toISOString(),
  };
};

const toEntityId = (entityType: string, name: string): string => {
  return `${entityType.toLowerCase()}:${name.toLowerCase().replace(/\s+/g, '_')}`;
};

const toEdgeId = (source: string, target: string, relation: string): string => {
  return `${source}-${relation}->${target}`;
};

const layoutGraph = (
  nodesById: Map<string, RetrievalGraphNode>,
  edgesById: Map<string, RetrievalGraphEdge>,
): { nodes: RetrievalGraphNode[]; edges: RetrievalGraphEdge[] } => {
  const order = ['Person', 'Project', 'Methodology', 'Industry', 'Document'];
  const spacingX = 230;
  const spacingY = 96;

  const grouped = new Map<string, RetrievalGraphNode[]>();
  nodesById.forEach((node) => {
    const key = node.data.entityType || 'Entity';
    const list = grouped.get(key) || [];
    list.push(node);
    grouped.set(key, list);
  });

  order.forEach((key) => {
    const list = grouped.get(key);
    if (list) {
      list.sort((a, b) => a.data.label.localeCompare(b.data.label));
    }
  });

  const fallbackGroups = Array.from(grouped.entries())
    .filter(([key]) => !order.includes(key))
    .sort((a, b) => a[0].localeCompare(b[0]));

  const columns = [
    ...order.map((key) => ({ key, nodes: grouped.get(key) || [] })),
    ...fallbackGroups.map(([key, nodes]) => ({ key, nodes })),
  ].filter((entry) => entry.nodes.length > 0);

  const positioned: RetrievalGraphNode[] = [];
  columns.forEach((column, columnIndex) => {
    column.nodes.forEach((node, rowIndex) => {
      positioned.push({
        ...node,
        position: {
          x: 40 + spacingX * columnIndex,
          y: 30 + spacingY * rowIndex,
        },
      });
    });
  });

  const edges = Array.from(edgesById.values());
  return { nodes: positioned, edges };
};

const extractGraphResult = (event: AgentChatStreamEvent): Record<string, unknown> | null => {
  if (event.data && typeof event.data === 'object') {
    const root = event.data as Record<string, unknown>;
    if (root.graph_result && typeof root.graph_result === 'object') {
      return root.graph_result as Record<string, unknown>;
    }

    const retrievalOutput = root.retrieval_output;
    if (
      retrievalOutput &&
      typeof retrievalOutput === 'object' &&
      (retrievalOutput as Record<string, unknown>).graph_result &&
      typeof (retrievalOutput as Record<string, unknown>).graph_result === 'object'
    ) {
      return (retrievalOutput as Record<string, unknown>).graph_result as Record<string, unknown>;
    }
  }
  return null;
};

const mergeGraphData = (
  state: RetrievalState,
  graphResult: Record<string, unknown> | null,
): Pick<RetrievalState, 'graphNodes' | 'graphEdges'> => {
  if (!graphResult) {
    return { graphNodes: state.graphNodes, graphEdges: state.graphEdges };
  }

  const nextNodes = new Map<string, RetrievalGraphNode>();
  state.graphNodes.forEach((node) => nextNodes.set(node.id, node));

  const nextEdges = new Map<string, RetrievalGraphEdge>();
  state.graphEdges.forEach((edge) => nextEdges.set(edge.id, edge));

  const projects = Array.isArray(graphResult.projects) ? graphResult.projects : [];

  projects.forEach((projectRaw) => {
    if (!projectRaw || typeof projectRaw !== 'object') {
      return;
    }
    const project = projectRaw as Record<string, unknown>;
    const projectName = normalizeString(project.name);
    if (!projectName) {
      return;
    }

    const projectId = toEntityId('Project', projectName);
    nextNodes.set(projectId, {
      id: projectId,
      position: { x: 0, y: 0 },
      data: {
        label: projectName,
        entityType: 'Project',
        attributes: normalizeAttributes(project.attributes),
        confidence: typeof project.confidence === 'number' ? project.confidence : undefined,
        isPrimary: true,
      },
    });

    const pushEntity = (
      rawEntities: unknown,
      entityType: 'Person' | 'Methodology' | 'Industry',
      relationshipType: string,
    ) => {
      if (!Array.isArray(rawEntities)) {
        return;
      }
      rawEntities.forEach((entityRaw) => {
        if (!entityRaw || typeof entityRaw !== 'object') {
          return;
        }
        const entity = entityRaw as Record<string, unknown>;
        const entityName = normalizeString(entity.name);
        if (!entityName) {
          return;
        }

        const entityId = toEntityId(entityType, entityName);
        nextNodes.set(entityId, {
          id: entityId,
          position: { x: 0, y: 0 },
          data: {
            label: entityName,
            entityType,
            attributes: normalizeAttributes(entity.attributes),
            confidence: typeof entity.confidence === 'number' ? entity.confidence : undefined,
          },
        });

        const edgeId = toEdgeId(entityId, projectId, relationshipType);
        nextEdges.set(edgeId, {
          id: edgeId,
          source: entityId,
          target: projectId,
          data: { relationshipType },
        });
      });
    };

    pushEntity(project.people, 'Person', 'WORKED_ON');
    pushEntity(project.methodologies, 'Methodology', 'USED_METHODOLOGY');
    pushEntity(project.industries, 'Industry', 'IN_INDUSTRY');

    if (Array.isArray(project.documents)) {
      project.documents.forEach((docRaw) => {
        const docIdRaw = normalizeString(docRaw);
        if (!docIdRaw) {
          return;
        }
        const docNodeId = toEntityId('Document', docIdRaw);
        nextNodes.set(docNodeId, {
          id: docNodeId,
          position: { x: 0, y: 0 },
          data: {
            label: docIdRaw,
            entityType: 'Document',
            attributes: { id: docIdRaw },
          },
        });

        const edgeId = toEdgeId(docNodeId, projectId, 'RELATED_TO');
        nextEdges.set(edgeId, {
          id: edgeId,
          source: docNodeId,
          target: projectId,
          data: { relationshipType: 'RELATED_TO' },
        });
      });
    }
  });

  const layout = layoutGraph(nextNodes, nextEdges);
  return {
    graphNodes: layout.nodes,
    graphEdges: layout.edges,
  };
};

const normalizeSourceChunk = (chunkRaw: Record<string, unknown>): RetrievalContextSource | null => {
  const citationRef = normalizeString(chunkRaw.citation_ref);
  if (!citationRef) {
    return null;
  }
  const sourceTitle =
    normalizeString(chunkRaw.source_document_title) ||
    normalizeString(chunkRaw.source_title) ||
    normalizeString(chunkRaw.source_document_id) ||
    citationRef;

  return {
    citationRef,
    sourceTitle,
    sourceSnippet: normalizeString(chunkRaw.text) || normalizeString(chunkRaw.source_snippet) || '',
    relevanceScore:
      typeof chunkRaw.relevance_score === 'number'
        ? chunkRaw.relevance_score
        : typeof chunkRaw.final_score === 'number'
          ? chunkRaw.final_score
          : undefined,
    sourceDocumentId: normalizeString(chunkRaw.source_document_id) || undefined,
    metadata: normalizeAttributes(chunkRaw.metadata),
  };
};

const extractSourceChunks = (event: AgentChatStreamEvent): RetrievalContextSource[] => {
  const data = event.data as Record<string, unknown>;
  const chunks: RetrievalContextSource[] = [];

  const pushChunks = (raw: unknown) => {
    if (!Array.isArray(raw)) {
      return;
    }
    raw.forEach((item) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const normalized = normalizeSourceChunk(item as Record<string, unknown>);
      if (normalized) {
        chunks.push(normalized);
      }
    });
  };

  pushChunks((data.assembled_context as Record<string, unknown> | undefined)?.source_chunks);
  pushChunks((data.retrieval_result as Record<string, unknown> | undefined)?.chunks);
  const retrievalOutput = data.retrieval_output as Record<string, unknown> | undefined;
  pushChunks(
    (retrievalOutput?.assembled_context as Record<string, unknown> | undefined)?.source_chunks,
  );

  const citation = data.citation;
  if (citation && typeof citation === 'object') {
    const citationPayload = citation as Record<string, unknown>;
    const citationRef = normalizeString(citationPayload.citation_ref);
    if (citationRef) {
      chunks.push({
        citationRef,
        sourceTitle:
          normalizeString(citationPayload.source_title) ||
          normalizeString(citationPayload.citation_ref) ||
          citationRef,
        sourceSnippet: normalizeString(citationPayload.source_snippet) || '',
        relevanceScore:
          typeof citationPayload.confidence === 'number' ? citationPayload.confidence : undefined,
      });
    }
  }

  return chunks;
};

const normalizeCitation = (citationRaw: unknown): AgentCitation | null => {
  if (!citationRaw || typeof citationRaw !== 'object') {
    return null;
  }
  const citation = citationRaw as Record<string, unknown>;
  const citationRef = normalizeString(citation.citation_ref);
  const path = normalizeString(citation.path) || citationRef || 'source';
  const startLine =
    typeof citation.start_line === 'number'
      ? citation.start_line
      : typeof citation.start_pos === 'number'
        ? citation.start_pos
        : 0;
  const endLine =
    typeof citation.end_line === 'number'
      ? citation.end_line
      : typeof citation.end_pos === 'number'
        ? citation.end_pos
        : startLine;

  return {
    path,
    start_line: startLine,
    end_line: endLine,
    text: normalizeString(citation.text) || normalizeString(citation.quote) || citationRef || path,
  };
};

const normalizeVerification = (data: Record<string, unknown>): RetrievalVerification | null => {
  if (typeof data.passed === 'boolean' && Array.isArray(data.checks)) {
    const checks = data.checks
      .filter((item) => item && typeof item === 'object')
      .map((item) => {
        const check = item as Record<string, unknown>;
        return {
          ruleName:
            normalizeString(check.rule_name) || normalizeString(check.name) || 'verification',
          passed: Boolean(check.passed),
          severity:
            (normalizeString(check.severity) as 'error' | 'warning' | 'info' | null) || 'info',
          message: normalizeString(check.message) || '',
        };
      });
    return {
      passed: Boolean(data.passed),
      summary: normalizeString(data.summary) || '',
      checks,
    };
  }

  const candidate = data.verification as Record<string, unknown> | undefined;
  if (!candidate) {
    return null;
  }
  return normalizeVerification(candidate);
};

const computeConfidence = (generated: Record<string, unknown> | null): number | null => {
  if (!generated || typeof generated !== 'object') {
    return null;
  }

  const summary = generated.confidence_summary;
  if (!summary || typeof summary !== 'object') {
    return null;
  }

  const confidence = summary as Record<string, unknown>;
  const grounded = typeof confidence.grounded === 'number' ? confidence.grounded : 0;
  const weak = typeof confidence.weak === 'number' ? confidence.weak : 0;
  const ungrounded = typeof confidence.ungrounded === 'number' ? confidence.ungrounded : 0;
  const total = grounded + weak + ungrounded;
  if (total <= 0) {
    return null;
  }

  return Math.max(0, Math.min(1, (grounded + weak * 0.5) / total));
};

const updatePlanStep = (
  plan: RetrievalPlan | null,
  stepId: string,
  update: Partial<RetrievalPlanStep>,
): RetrievalPlan | null => {
  if (!plan) {
    return plan;
  }
  return {
    ...plan,
    updatedAt: new Date().toISOString(),
    steps: plan.steps.map((step) => (step.id === stepId ? { ...step, ...update } : step)),
  };
};

const markExecutionFromEvent = (
  state: RetrievalState,
  event: AgentChatStreamEvent,
): RetrievalPlan | null => {
  const eventType = event.event_type || event.event;
  const data = event.data as Record<string, unknown>;
  let plan = state.plan;
  if (!plan) {
    const goal = state.activeQuery || normalizeString(data.query) || 'Assist user request';
    plan = buildDefaultPlan(goal);
  }
  if (data.plan && typeof data.plan === 'object') {
    const parsed = toRetrievalPlan(data.plan);
    if (parsed) {
      plan = parsed;
    }
  }
  if (Array.isArray(data.steps) && plan) {
    const parsed = toRetrievalPlan({
      goal: plan.goal,
      steps: data.steps,
      approval: { required: plan.requiresApproval },
    });
    if (parsed) {
      plan = {
        ...parsed,
        id: plan.id,
        sessionId: plan.sessionId,
      };
    }
  }
  const incomingPlanId =
    normalizeString(event.plan_id) || normalizeString(data.plan_id) || normalizeString(data.id);
  const incomingSessionId = normalizeString(data.session_id);
  if (plan && (incomingPlanId || incomingSessionId)) {
    plan = {
      ...plan,
      id: incomingPlanId || plan.id,
      sessionId: incomingSessionId || plan.sessionId || null,
    };
  }

  const explicitStepId = normalizeString(event.step_id) || normalizeString(data.step_id) || null;
  if (explicitStepId && eventType === 'step_started') {
    plan = updatePlanStep(plan, explicitStepId, { status: 'running' });
  }
  if (explicitStepId && eventType === 'step_completed') {
    plan = updatePlanStep(plan, explicitStepId, { status: 'completed' });
  }

  if (eventType === 'step_progress') {
    if (normalizeString(data.query)) {
      plan = updatePlanStep(plan, 'analyze-query', { status: 'completed' });
      plan = updatePlanStep(plan, 'search-graph', { status: 'running' });
    }

    if (typeof data.total_chunks === 'number') {
      plan = updatePlanStep(plan, 'search-retrieval', {
        status: 'completed',
        resultSummary: `${data.total_chunks} chunks retrieved`,
      });
      plan = updatePlanStep(plan, 'generate-answer', { status: 'running' });
    }
  }

  if (eventType === 'graph_update') {
    plan = updatePlanStep(plan, 'search-graph', { status: 'completed' });
    plan = updatePlanStep(plan, 'search-retrieval', { status: 'running' });
  }

  if (eventType === 'text_delta') {
    plan = updatePlanStep(plan, 'generate-answer', { status: 'running' });
  }

  if (eventType === 'verification') {
    plan = updatePlanStep(plan, 'generate-answer', { status: 'completed' });
    plan = updatePlanStep(plan, 'verify-answer', { status: 'completed' });
  }

  if (eventType === 'complete') {
    const generated = data.generated as Record<string, unknown> | undefined;
    if (generated) {
      plan = updatePlanStep(plan, 'generate-answer', { status: 'completed' });
      plan = updatePlanStep(plan, 'verify-answer', { status: 'completed' });
    }
  }
  if (eventType === 'plan_completed') {
    plan = updatePlanStep(plan, 'verify-answer', { status: 'completed' });
  }

  if (eventType === 'error' && plan) {
    const runningStep = plan.steps.find((step) => step.status === 'running');
    if (runningStep) {
      plan = updatePlanStep(plan, runningStep.id, { status: 'failed' });
    }
  }

  return plan;
};

const buildTimelineEntry = (event: AgentChatStreamEvent): RetrievalTimelineEntry => {
  return {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    event: event.event || event.event_type || 'message',
    eventType: event.event_type || event.event || 'message',
    message: normalizeString(event.message) || normalizeString(event.event_type) || 'event',
    timestamp: normalizeString(event.timestamp) || new Date().toISOString(),
    data: (event.data || {}) as Record<string, unknown>,
  };
};

const reducer = (state: RetrievalState, action: RetrievalAction): RetrievalState => {
  switch (action.type) {
    case 'reset': {
      if (action.keepSession) {
        return {
          ...initialRetrievalState,
          sessionId: state.sessionId,
        };
      }
      return initialRetrievalState;
    }
    case 'set_session_id': {
      return {
        ...state,
        sessionId: action.sessionId,
        updatedAt: new Date().toISOString(),
      };
    }
    case 'start_query': {
      return {
        ...state,
        streamStatus: 'idle',
        activeQuery: action.query,
        lastError: null,
        plan: null,
        planOverlayVisible: false,
        isPlanEditing: false,
        executionPhase: 'idle',
        pendingPlanCommand: null,
        graphNodes: [],
        graphEdges: [],
        selectedNodeId: null,
        focusedCitationRef: null,
        contextSources: {},
        timeline: [],
        streamingText: '',
        generatedText: '',
        citations: [],
        gaps: [],
        verification: null,
        verificationResults: [],
        thinkingLogs: [],
        confidenceScore: null,
        updatedAt: new Date().toISOString(),
      };
    }
    case 'set_plan': {
      const executionPhase = !action.plan
        ? 'idle'
        : state.executionPhase === 'executing' || state.executionPhase === 'paused'
          ? state.executionPhase
          : state.streamStatus === 'completed'
            ? 'completed'
            : 'planned';
      return {
        ...state,
        streamStatus: state.streamStatus === 'error' ? 'error' : state.streamStatus,
        activeQuery: action.plan?.goal ?? state.activeQuery,
        plan: action.plan,
        executionPhase,
        planOverlayVisible: action.openOverlay,
        isPlanEditing: false,
        pendingPlanCommand: null,
        updatedAt: new Date().toISOString(),
      };
    }
    case 'select_node': {
      return {
        ...state,
        selectedNodeId: action.nodeId,
      };
    }
    case 'focus_citation': {
      return {
        ...state,
        focusedCitationRef: action.citationRef,
      };
    }
    case 'set_plan_overlay_visible': {
      return {
        ...state,
        planOverlayVisible: action.visible,
      };
    }
    case 'set_plan_editing': {
      return {
        ...state,
        isPlanEditing: action.editing,
      };
    }
    case 'set_execution_phase': {
      return {
        ...state,
        executionPhase: action.phase,
      };
    }
    case 'request_plan_command': {
      return {
        ...state,
        pendingPlanCommand: action.command,
      };
    }
    case 'acknowledge_plan_command': {
      if (!state.pendingPlanCommand || state.pendingPlanCommand.id !== action.commandId) {
        return state;
      }
      return {
        ...state,
        pendingPlanCommand: null,
      };
    }
    case 'ingest_event': {
      const event = action.event;
      const eventType = event.event_type || event.event || 'message';
      const data = (event.data || {}) as Record<string, unknown>;

      const timelineEntry = buildTimelineEntry(event);
      const timeline = [...state.timeline, timelineEntry].slice(-MAX_TIMELINE_ENTRIES);

      const mergedSources: Record<string, RetrievalContextSource> = { ...state.contextSources };
      extractSourceChunks(event).forEach((source) => {
        const existing = mergedSources[source.citationRef];
        mergedSources[source.citationRef] = {
          ...existing,
          ...source,
          sourceTitle: source.sourceTitle || existing?.sourceTitle || source.citationRef,
          sourceSnippet: source.sourceSnippet || existing?.sourceSnippet || '',
          relevanceScore:
            source.relevanceScore !== undefined ? source.relevanceScore : existing?.relevanceScore,
        };
      });

      const graph = mergeGraphData(state, extractGraphResult(event));

      let citations = state.citations;
      if (eventType === 'citation') {
        const citationFromEvent = normalizeCitation(data.citation);
        if (citationFromEvent) {
          citations = [...citations, citationFromEvent];
        }
        if (Array.isArray(data.citations)) {
          const normalized = data.citations
            .map((item) => normalizeCitation(item))
            .filter(Boolean) as AgentCitation[];
          citations = [...citations, ...normalized];
        }
      }

      let gaps = state.gaps;
      if (eventType === 'gap') {
        const gap = normalizeString(data.gap) || normalizeString(data.description);
        if (gap) {
          gaps = [...gaps, gap];
        }
      }

      let streamingText = state.streamingText;
      if (eventType === 'text_delta') {
        const text = normalizeString(data.text) || normalizeString(data.text_delta) || '';
        streamingText += text;
      }

      let generatedText = state.generatedText;
      if (eventType === 'complete') {
        const generated = data.generated as Record<string, unknown> | undefined;
        if (generated && normalizeString(generated.content)) {
          generatedText = normalizeString(generated.content) || generatedText;
          if (Array.isArray(generated.citations)) {
            citations = generated.citations
              .map((item) => normalizeCitation(item))
              .filter(Boolean) as AgentCitation[];
          }
          if (Array.isArray(generated.gaps)) {
            gaps = generated.gaps.map((item) => normalizeString(item)).filter(Boolean) as string[];
          }
        }
      }

      const verification = normalizeVerification(data) || state.verification;
      let verificationResults = state.verificationResults;
      if (
        verification &&
        (eventType === 'verification' || eventType === 'verification_completed')
      ) {
        verificationResults = [...verificationResults, verification];
      }

      let thinkingLogs = state.thinkingLogs;
      const stepIdForLog = normalizeString(event.step_id) || normalizeString(data.step_id) || null;
      const logMessage =
        normalizeString(event.message) ||
        normalizeString(data.summary) ||
        normalizeString(data.error);
      if (
        stepIdForLog &&
        logMessage &&
        (eventType === 'step_started' ||
          eventType === 'step_progress' ||
          eventType === 'step_completed' ||
          eventType === 'error')
      ) {
        const level: RetrievalThinkingLog['level'] =
          eventType === 'error' ? 'error' : eventType === 'step_progress' ? 'info' : 'info';
        thinkingLogs = [
          ...thinkingLogs,
          {
            id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
            stepId: stepIdForLog,
            level,
            message: logMessage,
            timestamp: normalizeString(event.timestamp) || new Date().toISOString(),
          },
        ].slice(-500);
      }

      let confidenceScore = state.confidenceScore;
      if (eventType === 'complete') {
        confidenceScore =
          computeConfidence(data.generated as Record<string, unknown> | null) ?? confidenceScore;
      }
      if (verification && confidenceScore === null) {
        confidenceScore = verification.passed ? 0.85 : 0.45;
      }

      const nextPlan = markExecutionFromEvent(state, event);

      let streamStatus = state.streamStatus;
      let lastError = state.lastError;
      let executionPhase = state.executionPhase;
      if (eventType === 'error') {
        streamStatus = 'error';
        lastError = normalizeString(data.error) || event.message || 'Stream failed';
        executionPhase = 'failed';
      } else if (eventType === 'complete' || eventType === 'plan_completed') {
        const isFinalComplete = typeof data.session_id === 'string' || Boolean(data.generated);
        streamStatus = isFinalComplete ? 'completed' : 'streaming';
        executionPhase = isFinalComplete ? 'completed' : 'executing';
      } else if (eventType === 'plan_ready' || eventType === 'plan_created') {
        streamStatus = 'idle';
        executionPhase = 'planned';
      } else {
        streamStatus = 'streaming';
        lastError = null;
        if (
          eventType === 'step_started' ||
          eventType === 'step_progress' ||
          eventType === 'step_completed' ||
          eventType === 'text_delta' ||
          eventType === 'citation' ||
          eventType === 'gap' ||
          eventType === 'verification'
        ) {
          executionPhase = 'executing';
        }
      }

      return {
        ...state,
        streamStatus,
        lastError,
        plan: nextPlan,
        executionPhase,
        contextSources: mergedSources,
        graphNodes: graph.graphNodes,
        graphEdges: graph.graphEdges,
        timeline,
        citations,
        gaps,
        streamingText,
        generatedText,
        verification,
        verificationResults,
        thinkingLogs,
        confidenceScore,
        updatedAt: new Date().toISOString(),
      };
    }
    case 'reorder_plan_steps': {
      if (!state.plan) {
        return state;
      }
      const fromIndex = action.fromIndex;
      const toIndex = action.toIndex;
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= state.plan.steps.length ||
        toIndex >= state.plan.steps.length
      ) {
        return state;
      }
      const steps = [...state.plan.steps];
      const [moved] = steps.splice(fromIndex, 1);
      steps.splice(toIndex, 0, moved);
      return {
        ...state,
        plan: {
          ...state.plan,
          steps,
          updatedAt: new Date().toISOString(),
        },
      };
    }
    case 'remove_plan_step': {
      if (!state.plan) {
        return state;
      }
      return {
        ...state,
        plan: {
          ...state.plan,
          steps: state.plan.steps.filter((step) => step.id !== action.stepId),
          updatedAt: new Date().toISOString(),
        },
      };
    }
    case 'add_plan_step': {
      if (!state.plan) {
        return state;
      }
      const steps = [...state.plan.steps];
      const index =
        typeof action.atIndex === 'number' && action.atIndex >= 0 && action.atIndex <= steps.length
          ? action.atIndex
          : steps.length;
      steps.splice(index, 0, action.step);
      return {
        ...state,
        plan: {
          ...state.plan,
          steps,
          updatedAt: new Date().toISOString(),
        },
      };
    }
    case 'update_plan_goal': {
      if (!state.plan) {
        return state;
      }
      return {
        ...state,
        plan: {
          ...state.plan,
          goal: action.goal,
          updatedAt: new Date().toISOString(),
        },
      };
    }
    default:
      return state;
  }
};

export const RetrievalStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialRetrievalState);

  const reset = useCallback((opts?: { keepSession?: boolean }) => {
    dispatch({ type: 'reset', keepSession: Boolean(opts?.keepSession) });
  }, []);

  const setSessionId = useCallback((sessionId: string | null) => {
    dispatch({ type: 'set_session_id', sessionId });
  }, []);

  const startQuery = useCallback((query: string) => {
    dispatch({ type: 'start_query', query });
  }, []);

  const setPlan = useCallback((plan: RetrievalPlan | null, opts?: { openOverlay?: boolean }) => {
    dispatch({ type: 'set_plan', plan, openOverlay: Boolean(opts?.openOverlay) });
  }, []);

  const ingestEvent = useCallback((event: AgentChatStreamEvent) => {
    dispatch({ type: 'ingest_event', event });
  }, []);

  const selectNode = useCallback((nodeId: string | null) => {
    dispatch({ type: 'select_node', nodeId });
  }, []);

  const focusCitationRef = useCallback((citationRef: string | null) => {
    dispatch({ type: 'focus_citation', citationRef });
  }, []);

  const setPlanOverlayVisible = useCallback((visible: boolean) => {
    dispatch({ type: 'set_plan_overlay_visible', visible });
  }, []);

  const setPlanEditing = useCallback((editing: boolean) => {
    dispatch({ type: 'set_plan_editing', editing });
  }, []);

  const setExecutionPhase = useCallback((phase: RetrievalExecutionPhase) => {
    dispatch({ type: 'set_execution_phase', phase });
  }, []);

  const requestPlanCommand = useCallback(
    (type: RetrievalPlanCommandType, payload?: Record<string, unknown>) => {
      const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      dispatch({
        type: 'request_plan_command',
        command: {
          id,
          type,
          payload,
        },
      });
      return id;
    },
    [],
  );

  const acknowledgePlanCommand = useCallback((commandId: string) => {
    dispatch({ type: 'acknowledge_plan_command', commandId });
  }, []);

  const reorderPlanSteps = useCallback((fromIndex: number, toIndex: number) => {
    dispatch({ type: 'reorder_plan_steps', fromIndex, toIndex });
  }, []);

  const removePlanStep = useCallback((stepId: string) => {
    dispatch({ type: 'remove_plan_step', stepId });
  }, []);

  const addPlanStep = useCallback((step: RetrievalPlanStep, atIndex?: number) => {
    dispatch({ type: 'add_plan_step', step, atIndex });
  }, []);

  const updatePlanGoal = useCallback((goal: string) => {
    dispatch({ type: 'update_plan_goal', goal });
  }, []);

  const value = useMemo<RetrievalStateContextValue>(
    () => ({
      state,
      reset,
      setSessionId,
      startQuery,
      setPlan,
      ingestEvent,
      selectNode,
      focusCitationRef,
      setPlanOverlayVisible,
      setPlanEditing,
      setExecutionPhase,
      requestPlanCommand,
      acknowledgePlanCommand,
      reorderPlanSteps,
      removePlanStep,
      addPlanStep,
      updatePlanGoal,
    }),
    [
      state,
      reset,
      setSessionId,
      startQuery,
      setPlan,
      ingestEvent,
      selectNode,
      focusCitationRef,
      setPlanOverlayVisible,
      setPlanEditing,
      setExecutionPhase,
      requestPlanCommand,
      acknowledgePlanCommand,
      reorderPlanSteps,
      removePlanStep,
      addPlanStep,
      updatePlanGoal,
    ],
  );

  return <RetrievalStateContext.Provider value={value}>{children}</RetrievalStateContext.Provider>;
};

export const useRetrievalState = (): RetrievalStateContextValue => {
  const ctx = useContext(RetrievalStateContext);
  if (!ctx) {
    throw new Error('useRetrievalState must be used within RetrievalStateProvider');
  }
  return ctx;
};
